import React, { FC, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Grid, Switch, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ContentSharePreviewFragment, SocialAccount } from 'api';
import { SocialAccountIcon } from 'components';

import { INetwork, IShare } from '../types';
import { connectionPopup } from 'helpers';
import { useCurrentUser, useFeatures } from 'hooks';
import { Accounts } from './';
import Commentary from './Commentary';

interface IProps {
  accounts: SocialAccount[];
  network: INetwork;
  onConnect: (network: INetwork) => void;
  previewInfo?: ContentSharePreviewFragment;
}

const useStyles = makeStyles(theme => ({
  errorButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
}));

const ActiveAuth: FC<IProps> = props => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { accounts, network, onConnect, previewInfo } = props;
  const { values, setFieldValue } = useFormikContext<IShare>();
  const { id } = useCurrentUser();
  const { legacyAuthDomain } = useFeatures();

  const { provider } = network;

  const item = values[provider];
  const { active, network_id } = item;
  const selectedAccount = accounts.find(account => account.id === network_id);

  useEffect(() => {
    if (selectedAccount?.expires_soon) {
      setFieldValue(`${network.provider}.active`, false);
    }
  }, []);

  function toggleNetwork(event: ChangeEvent<HTMLInputElement>) {
    setFieldValue(`${network.provider}.active`, event.target.checked);
  }

  return (
    <Grid container alignItems="baseline" justify="space-between">
      <Grid item>
        <SocialAccountIcon size={24} active={active} provider={provider} />
      </Grid>
      <Grid item xs>
        <Grid container alignItems="center">
          <Grid item xs>
            {selectedAccount && (
              <Accounts
                accounts={accounts}
                selectedAccount={selectedAccount}
                onConnect={() => onConnect(network)}
                onChange={(account: SocialAccount) => {
                  setFieldValue(`${provider}.network_id`, account.id);
                }}
              />
            )}
          </Grid>
          <Grid item>
            {selectedAccount?.expires_soon ? (
              <Button
                className={classes.errorButton}
                variant="outlined"
                onClick={() => connectionPopup(id, provider, legacyAuthDomain)}
              >
                {t('sharing:reconnectToken')}
              </Button>
            ) : (
              <Switch checked={active} onChange={toggleNetwork} />
            )}
          </Grid>
        </Grid>
        {active && (
          <Grid container item xs>
            <Commentary network={network} previewInfo={previewInfo} />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default ActiveAuth;
